/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2019, 2023
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import {
  getActionSkillId,
  getAssistantId
} from '@ibm-watson/shared/modules/ia/store/session/selectors';
import { selectIsAccessUpdatesEnabled } from '../../../ia/store/features/selectors';
import { selectIsNewIa } from '../../../ia/store/instance/selectors';
import TrainingStatusPollingMachine, {
  SkillTrainingStatus
} from '../../util/trainingStatusPollingMachine';
import * as actionTypes from './actionTypes';

export const setTryitState = newState => ({
  type: actionTypes.SET_TRYIT_STATE,
  newState
});

export const setSkillImported = imported => ({
  type: actionTypes.SKILL_IMPORTED,
  imported
});

export const setTrainingState = newState => ({
  type: actionTypes.SET_TRAINING_STATE,
  newState
});

export const setEditPanelOpen = isOpen => ({
  type: actionTypes.SET_EDIT_PANEL_OPEN,
  isOpen
});

/**
 * Set the state of the try it panel. If newState is not provided then the state will be toggled.
 * @param {boolean} newState the new state (true for open otherwise false)
 * @return {function} a thunk
 */
export const toggleTryItState = newState => (dispatch, getState) => {
  if (typeof newState !== 'boolean') {
    const { workspacestate } = getState();
    newState = !workspacestate.tryitOpen;
  }
  dispatch(setTryitState(newState));
};

/**
 * @type {TrainingStatusPollingMachine?}
 */
export let pollingMachine = null;

export const pollSkillTrainingStatus =
  (skill_id?: string) => (dispatch, getState) => {
    if (pollingMachine instanceof TrainingStatusPollingMachine) {
      pollingMachine.stop();
    }
    const skillId = skill_id || getActionSkillId(getState());
    if (!skillId) {
      return;
    }
    // Use revised API for Access Control improvements if enabled & isNewIA
    // TODO when accessUpdates flag is removed, replace useNewIAUpdatedAccess with simpler isNewIA on create of TrainingStatusPollingMachine
    const assistantId = getAssistantId(getState());
    const isNewIA = selectIsNewIa(getState());
    const useNewIAUpdatedAccess =
      isNewIA && selectIsAccessUpdatesEnabled(getState());
    pollingMachine = new TrainingStatusPollingMachine(
      skillId,
      useNewIAUpdatedAccess,
      assistantId
    );

    pollingMachine.onTrainingStatusChange(status => {
      dispatch(setTrainingState(status));
    });

    pollingMachine.start();
  };

export const stopPolling = () => dispatch => {
  if (!(pollingMachine instanceof TrainingStatusPollingMachine)) {
    return;
  }
  pollingMachine.stop();
  pollingMachine = null;
  dispatch(setTrainingState(SkillTrainingStatus.NONE));
};
